import React, { useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetDemographics } from "../../features/patient-stats/demographics/model/use-get-demographics";
import { getDemographics } from "../../entities/patient-stats/demographics/demographics.selectors";
import { SectionSidebarNav } from "../../shared/ui/section-sidebar-nav/section-sidebar-nav";

import * as Styled from "./demographics.styled";

const DEMOGRAPHICS_NAV_SECTIONS = [
  { id: "patient-overview", label: "Patient Overview" },
  { id: "care-team-providers", label: "Care Team & Providers" },
  { id: "home-address-contacts", label: "Home Address & Contacts" },
  { id: "office-address-contacts", label: "Office Address & Contacts" },
  { id: "employment", label: "Employment" },
  { id: "guarantor", label: "Guarantor" },
  {
    id: "guarantor-home-address-contacts",
    label: "Guarantor Home Address & Contacts",
  },
  {
    id: "guarantor-office-address-contacts",
    label: "Guarantor Office Address & Contacts",
  },
  { id: "local-pharmacy", label: "Local Pharmacy" },
  { id: "administrative-info", label: "Administrative Info" },
  { id: "contacts", label: "Contacts" },
];

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Demographics = () => {
  const patientId = useParams().patientID;
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);

  const { getDemographicsByPatientId } = useGetDemographics();

  useEffect(() => {
    if (!patientId) return;
    getDemographicsByPatientId(patientId);
  }, [patientId]);

  useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const demographics = useSelector(getDemographics);

  if (!demographics) return null;

  return (
    <Styled.Container>
      <Styled.PageHeader>
        <Styled.Title>Demographics</Styled.Title>
        <Styled.Download>
          <div>Download All</div>
          <Styled.DownloadOptions>
            <a
              href={demographics.ui?.downloadLinks?.csv || "#"}
              download="demographics.csv"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M11 19.9998V11.002C11 9.89651 11.8968 9.00072 13.0022 9.00195L19.9958 9.00973L27 15.9998V19.9998M20 9.49975V13.9998C20 15.1043 20.8954 15.9998 22 15.9998H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 23H18C17.4477 23 17 23.4477 17 24V25C17 25.5523 17.4477 26 18 26H19C19.5523 26 20 26.4477 20 27V28C20 28.5523 19.5523 29 19 29H17M22.5 23L24.75 29L27 23M14.5 28C14.5 28.5523 14.0523 29 13.5 29H12C11.4477 29 11 28.5523 11 28V24C11 23.4477 11.4477 23 12 23H13.5C14.0523 23 14.5 23.4477 14.5 24"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>

            <a
              href={demographics.ui?.downloadLinks?.xls || "#"}
              download="demographics.xlsx"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M17.5 23V29H21"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 23L13 26M13 26L15 29M13 26L15 23M13 26L11 29"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M27.0002 23H23.5V26H27.0002V29H23.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 19.9978L11.0199 10.9956C11.0224 9.89186 11.9184 8.99877 13.0222 9L19.9958 9.00776L27 15.9948L26.9922 19.9978M20 9.49778V13.9978C20 15.1024 20.8954 15.9978 22 15.9978H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>

            <a
              href={demographics.ui?.downloadLinks?.pdf || "#"}
              download="demographics.pdf"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M11 19.9978L11.0199 10.9956C11.0224 9.89186 11.9185 8.99877 13.0222 9L19.9958 9.00776L27 15.9948L26.9922 19.9978M20 9.49778V13.9978C20 15.1024 20.8954 15.9978 22 15.9978H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M26.5 23.75C26.9142 23.75 27.25 23.4142 27.25 23C27.25 22.5858 26.9142 22.25 26.5 22.25V23.75ZM23 23V22.25C22.5858 22.25 22.25 22.5858 22.25 23H23ZM22.25 29C22.25 29.4142 22.5858 29.75 23 29.75C23.4142 29.75 23.75 29.4142 23.75 29H22.25ZM26 26.75C26.4142 26.75 26.75 26.4142 26.75 26C26.75 25.5858 26.4142 25.25 26 25.25V26.75ZM11 23V22.25C10.5858 22.25 10.25 22.5858 10.25 23H11ZM10.25 29C10.25 29.4142 10.5858 29.75 11 29.75C11.4142 29.75 11.75 29.4142 11.75 29H10.25ZM17 23V22.25C16.5858 22.25 16.25 22.5858 16.25 23H17ZM17 29H16.25C16.25 29.4142 16.5858 29.75 17 29.75V29ZM26.5 22.25H23V23.75H26.5V22.25ZM22.25 23V26H23.75V23H22.25ZM22.25 26V29H23.75V26H22.25ZM23 26.75H26V25.25H23V26.75ZM11 23.75H12.75V22.25H11V23.75ZM11.75 29V26.5H10.25V29H11.75ZM11.75 26.5V23H10.25V26.5H11.75ZM12.75 25.75H11V27.25H12.75V25.75ZM13.75 24.75C13.75 25.3023 13.3023 25.75 12.75 25.75V27.25C14.1307 27.25 15.25 26.1307 15.25 24.75H13.75ZM12.75 23.75C13.3023 23.75 13.75 24.1977 13.75 24.75H15.25C15.25 23.3693 14.1307 22.25 12.75 22.25V23.75ZM17 23.75H18.5V22.25H17V23.75ZM19.75 25V27H21.25V25H19.75ZM18.5 28.25H17V29.75H18.5V28.25ZM17.75 29V23H16.25V29H17.75ZM19.75 27C19.75 27.6904 19.1904 28.25 18.5 28.25V29.75C20.0188 29.75 21.25 28.5188 21.25 27H19.75ZM18.5 23.75C19.1904 23.75 19.75 24.3096 19.75 25H21.25C21.25 23.4812 20.0188 22.25 18.5 22.25V23.75Z"
                  fill="#181818"
                />
              </svg>
            </a>
          </Styled.DownloadOptions>
        </Styled.Download>
      </Styled.PageHeader>
      <Styled.InnerContainer>
        <SectionSidebarNav
          ariaLabel="Demographics sections"
          sections={DEMOGRAPHICS_NAV_SECTIONS}
          scrollContainerRef={contentScrollerRef}
        />
        <Styled.ContentScroller ref={contentScrollerRef}>
          <Styled.EntityContainer>
          <Styled.Entity>
            <Styled.EntityTitle id="patient-overview">
              Patient Overview
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.Row>
                <Styled.Description>
                  Full Name (first name, middle initial, last name, suffix)
                </Styled.Description>
                <Styled.Value>{demographics.fullName}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Date of Birth</Styled.Description>
                  <Styled.Value>{demographics.dateOfBirth}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Time of Birth</Styled.Description>
                  <Styled.Value>{demographics.timeOfBirth}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Sex</Styled.Description>
                  <Styled.Value>{demographics.sex}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Race</Styled.Description>
                  <Styled.Value>{demographics.race}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Ethnicity Code</Styled.Description>
                  <Styled.Value>{demographics.ethnicityCode}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Language</Styled.Description>
                  <Styled.Value>{demographics.language}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Marital Status</Styled.Description>
                <Styled.Value>{demographics.maritalStatus}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Patient Number</Styled.Description>
                  <Styled.Value>{demographics.patientNumber}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>
                    Social Security Number
                  </Styled.Description>
                  <Styled.Value>
                    {demographics.socialSecurityNumber}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>
                    Preferred Name (first name, middle initial, last name)
                  </Styled.Description>
                  <Styled.Value>{demographics.preferredName}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>
                    Former Name (first name, middle initial, last name)
                  </Styled.Description>
                  <Styled.Value>{demographics.formerName}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Nickname</Styled.Description>
                <Styled.Value>{demographics.nickname}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Birth State Code</Styled.Description>
                  <Styled.Value>{demographics.birthStateCode}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Birth Country Code</Styled.Description>
                  <Styled.Value>{demographics.birthCountryCode}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Blood Type</Styled.Description>
                <Styled.Value>{demographics.bloodType}</Styled.Value>
              </Styled.Row>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="care-team-providers">
              Care Team & Providers
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Referring Provider</Styled.Description>
                  <Styled.Value>{demographics.referringProvider}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>
                    Referring Provider Phone
                  </Styled.Description>
                  <Styled.Value>
                    {demographics.referringProviderPhone}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>PCP Provider</Styled.Description>
                  <Styled.Value>{demographics.pcpProvider}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>PCP Provider Phone</Styled.Description>
                  <Styled.Value>{demographics.pcpProviderPhone}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Usual Caregiver</Styled.Description>
                  <Styled.Value>{demographics.usualCaregiver}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Usual Caregiver Phone</Styled.Description>
                  <Styled.Value>
                    {demographics.usualCaregiverPhone}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Privacy Status</Styled.Description>
                <Styled.Value>{demographics.privacyStatus}</Styled.Value>
              </Styled.Row>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="home-address-contacts">
              Home Address & Contacts
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Address</Styled.Description>
                  <Styled.Value>{demographics.homeAddress}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>City / State / Zip</Styled.Description>
                  <Styled.Value>{demographics.homeCityStateZip}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>County</Styled.Description>
                  <Styled.Value>{demographics.homeCounty}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Cross Street</Styled.Description>
                <Styled.Value>{demographics.homeCrossStreet}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Phone</Styled.Description>
                  <Styled.Value>{demographics.homePhone}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Car Phone</Styled.Description>
                  <Styled.Value>{demographics.homeCarPhone}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Fax</Styled.Description>
                  <Styled.Value>{demographics.homeFax}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Pager</Styled.Description>
                  <Styled.Value>{demographics.homePager}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Email</Styled.Description>
                <Styled.Value>{demographics.homeEmail}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Start Date</Styled.Description>
                  <Styled.Value>{demographics.homeStartDate}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>End Date</Styled.Description>
                  <Styled.Value>{demographics.homeEndDate}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="office-address-contacts">
              Office Address & Contacts
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Address</Styled.Description>
                  <Styled.Value>{demographics.officeAddress}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>City / State / Zip</Styled.Description>
                  <Styled.Value>{demographics.officeCityStateZip}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>County</Styled.Description>
                  <Styled.Value>{demographics.officeCounty}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Cross Street</Styled.Description>
                <Styled.Value>{demographics.officeCrossStreet}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Phone</Styled.Description>
                  <Styled.Value>{demographics.officePhone}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Car Phone</Styled.Description>
                  <Styled.Value>{demographics.officeCarPhone}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Fax</Styled.Description>
                  <Styled.Value>{demographics.officeFax}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Pager</Styled.Description>
                  <Styled.Value>{demographics.officePager}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Email</Styled.Description>
                <Styled.Value>{demographics.officeEmail}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Start Date</Styled.Description>
                  <Styled.Value>{demographics.officeStartDate}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>End Date</Styled.Description>
                  <Styled.Value>{demographics.officeEndDate}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="employment">Employment</Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Employer</Styled.Description>
                  <Styled.Value>{demographics.employer}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Employer Status</Styled.Description>
                  <Styled.Value>{demographics.employerStatus}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Occupation</Styled.Description>
                  <Styled.Value>{demographics.occupation}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Industry</Styled.Description>
                  <Styled.Value>{demographics.industry}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="guarantor">Guarantor</Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>
                    Full Name (first name, middle initial, last name, suffix)
                  </Styled.Description>
                  <Styled.Value>{demographics.guarantorFullName}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Relationship</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorRelationship}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Sex</Styled.Description>
                <Styled.Value>{demographics.guarantorSex}</Styled.Value>
              </Styled.Row>

              <Styled.Row>
                <Styled.Description>Date of Birth</Styled.Description>
                <Styled.Value>{demographics.guarantorDateOfBirth}</Styled.Value>
              </Styled.Row>

              <Styled.Row>
                <Styled.Description>Social Security Number</Styled.Description>
                <Styled.Value>{demographics.guarantorSSN}</Styled.Value>
              </Styled.Row>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="guarantor-home-address-contacts">
              Guarantor Home Address & Contacts
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Address</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorHomeAddress}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>City / State / Zip</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorHomeCityStateZip}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>County</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorHomeCounty}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Cross Street</Styled.Description>
                <Styled.Value>
                  {demographics.guarantorHomeCrossStreet}
                </Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Phone</Styled.Description>
                  <Styled.Value>{demographics.guarantorHomePhone}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Car Phone</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorHomeCarPhone}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Fax</Styled.Description>
                  <Styled.Value>{demographics.guarantorHomeFax}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Pager</Styled.Description>
                  <Styled.Value>{demographics.guarantorHomePager}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Email</Styled.Description>
                <Styled.Value>{demographics.guarantorHomeEmail}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Start Date</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorHomeStartDate}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>End Date</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorHomeEndDate}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="guarantor-office-address-contacts">
              Guarantor Office Address & Contacts
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Address</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficeAddress}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>City / State / Zip</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficeCityStateZip}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>County</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficeCounty}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Cross Street</Styled.Description>
                <Styled.Value>
                  {demographics.guarantorOfficeCrossStreet}
                </Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Phone</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficePhone}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Car Phone</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficeCarPhone}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Fax</Styled.Description>
                  <Styled.Value>{demographics.guarantorOfficeFax}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Pager</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficePager}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Email</Styled.Description>
                <Styled.Value>{demographics.guarantorOfficeEmail}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Start Date</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficeStartDate}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>End Date</Styled.Description>
                  <Styled.Value>
                    {demographics.guarantorOfficeEndDate}
                  </Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="local-pharmacy">
              Local Pharmacy
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.Row>
                <Styled.Description>Name</Styled.Description>
                <Styled.Value>{demographics.pharmacyName}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Address</Styled.Description>
                  <Styled.Value>{demographics.pharmacyAddress}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>City / State / Zip</Styled.Description>
                  <Styled.Value>
                    {demographics.pharmacyCityStateZip}
                  </Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>County</Styled.Description>
                  <Styled.Value>{demographics.pharmacyCounty}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Cross Street</Styled.Description>
                <Styled.Value>{demographics.pharmacyCrossStreet}</Styled.Value>
              </Styled.Row>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Phone</Styled.Description>
                  <Styled.Value>{demographics.pharmacyPhone}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Car Phone</Styled.Description>
                  <Styled.Value>{demographics.pharmacyCarPhone}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Fax</Styled.Description>
                  <Styled.Value>{demographics.pharmacyFax}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Pager</Styled.Description>
                  <Styled.Value>{demographics.pharmacyPager}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Email</Styled.Description>
                <Styled.Value>{demographics.pharmacyEmail}</Styled.Value>
              </Styled.Row>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="status-alerts">
              Status & Alerts
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Status</Styled.Description>
                  <Styled.Value>{demographics.status}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Patient Type</Styled.Description>
                  <Styled.Value>{demographics.patientType}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Medic Alert</Styled.Description>
                <Styled.Value>{demographics.medicAlert}</Styled.Value>
              </Styled.Row>

              <Styled.Row>
                <Styled.Description>Organ Donor</Styled.Description>
                <Styled.Value>{demographics.organDonor}</Styled.Value>
              </Styled.Row>

              <Styled.Row>
                <Styled.Description>VIP Indicator</Styled.Description>
                <Styled.Value>{demographics.vipIndicator}</Styled.Value>
              </Styled.Row>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="administrative-info">
              Administrative Info
            </Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Chart ID</Styled.Description>
                  <Styled.Value>{demographics.chartId}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Account ID</Styled.Description>
                  <Styled.Value>{demographics.accountId}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Release of Info</Styled.Description>
                  <Styled.Value>{demographics.releaseOfInfo}</Styled.Value>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>HCFA Signature</Styled.Description>
                  <Styled.Value>{demographics.hcfaSignature}</Styled.Value>
                </Styled.Row>
              </Styled.DividedRow>

              <Styled.Row>
                <Styled.Description>Notify Method</Styled.Description>
                <Styled.Value>{demographics.notifyMethod}</Styled.Value>
              </Styled.Row>
            </Styled.RowedData>
          </Styled.Entity>

          <Styled.Entity>
            <Styled.EntityTitle id="contacts">Contacts</Styled.EntityTitle>
            <Styled.RowedData>
              <Styled.DividedRow>
                <Styled.Row>
                  <Styled.Description>Last Name</Styled.Description>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>First Name</Styled.Description>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Middle Name</Styled.Description>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Relationship</Styled.Description>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Emergency</Styled.Description>
                </Styled.Row>
                <Styled.Row>
                  <Styled.Description>Phone</Styled.Description>
                </Styled.Row>
              </Styled.DividedRow>

              {demographics.contacts.map((contact, index) => (
                <Styled.DividedRow
                  key={`${contact.lastName}-${contact.firstName}-${index}`}
                >
                  <Styled.Row>
                    <Styled.Value>{contact.lastName}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Value>{contact.firstName}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Value>{contact.middleName}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Value>{contact.relationship}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Value>{contact.emergency}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Value>{contact.phone}</Styled.Value>
                  </Styled.Row>
                </Styled.DividedRow>
              ))}
            </Styled.RowedData>
          </Styled.Entity>
          </Styled.EntityContainer>
        </Styled.ContentScroller>
      </Styled.InnerContainer>
    </Styled.Container>
  );
};
